import React, { useEffect, useRef, useState } from 'react';
import Overlay from './Overlay.jsx';
import { NOTE_MAX, PROBLEM_KINDS, makeRequestId, problemPayload, recordPalletCheck } from './palletCheckData';
import { photoForCheck } from './photo';
import { C } from './styles';

// "Something's wrong": what doesn't match (six big choices), how many they counted (count only), a note,
// a photo of the pallet, their PIN, "Tell the office" -> POST .../checks {outcome: 'problem', ...}. The
// API posts it to shop alerts and flags the pallet until a manager clears it.

const choiceStyle = (on) => ({
  textAlign: 'left', minHeight: 60, borderRadius: 12, border: on ? `3px solid ${C.red}` : `1px solid ${C.dim}`,
  background: on ? '#FEF2F2' : '#FFFFFF', color: C.ink, fontFamily: 'inherit', padding: '10px 14px', display: 'flex',
  flexDirection: 'column', gap: 2, cursor: 'pointer', width: '100%',
});
const input = { minHeight: 52, borderRadius: 10, border: `1px solid ${C.steel}`, padding: '0 14px', fontFamily: 'inherit', fontSize: 20, background: '#FFFFFF', boxSizing: 'border-box', width: '100%' };

export default function ProblemForm({ open, onClose, palletId, palletShortId, orderNumber, expectedUnits, onSent, noun = 'pallet' }) {
  const [kind, setKind] = useState(null);
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState(null); // data: URL
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  // One client_request_id per report, sent again on every retry after an error or timeout (the API answers
  // `duplicate: true` for one it already recorded); a fresh one only after a report went through.
  const requestIdRef = useRef(null);

  useEffect(() => {
    if (open) { setKind(null); setCounted(''); setNote(''); setPhoto(null); setPin(''); setError(null); setBusy(false); requestIdRef.current = null; }
  }, [open]);
  if (!open) return null;

  const pickPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { dataUrl } = await photoForCheck(file);
      setPhoto(dataUrl);
    } catch (err) {
      setError("Couldn't read that photo. Try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!requestIdRef.current) requestIdRef.current = makeRequestId();
    const built = problemPayload({ kind, counted, note, photo, pin, requestId: requestIdRef.current });
    if (built.error) { setError(built.error); return; }
    setBusy(true); setError(null);
    const r = await recordPalletCheck(palletId, built.payload);
    setBusy(false);
    if (!r.ok) { setError(r.message); return; }
    requestIdRef.current = null;
    setPin('');
    onSent(r.body || {});
  };

  return (
    <Overlay open={open} onClose={onClose} dark={false} monoTitle={false} title="Something's wrong" subtitle={[palletShortId, orderNumber].filter(Boolean).join(' · ')}
      closeLabel={`Back to the ${noun}`} headerStyle={{ background: C.red, color: '#FFFFFF' }} testId="problem-form">
      <form onSubmit={submit} noValidate>
        <fieldset style={{ padding: '18px 16px 0', display: 'flex', flexDirection: 'column', gap: 10, border: 0, margin: 0 }}>
          <legend style={{ fontSize: 18, fontWeight: 700, padding: 0, marginBottom: 10 }}>What doesn't match?</legend>
          {PROBLEM_KINDS.map((c) => (
            <button key={c.kind} type="button" aria-pressed={kind === c.kind} onClick={() => setKind(c.kind)} style={choiceStyle(kind === c.kind)} data-kind={c.kind}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>{c.title}</span>
              <span style={{ fontSize: 14 }}>{c.sub.replace(/pallet/g, noun)}</span>
            </button>
          ))}
        </fieldset>

        {kind === 'count_off' && (
          <div style={{ padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="pc-counted" style={{ fontSize: 15, fontWeight: 600 }}>How many did you count?</label>
            <input id="pc-counted" inputMode="numeric" pattern="[0-9]*" value={counted} onChange={(e) => setCounted(e.target.value)}
              placeholder={expectedUnits != null ? `JobQ expects ${expectedUnits}` : ''} style={input} />
          </div>
        )}

        <div style={{ padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor="pc-note" style={{ fontSize: 15, fontWeight: 600 }}>{kind === 'other' ? 'What is wrong' : 'Anything else (optional)'}</label>
          <textarea id="pc-note" rows={3} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you see"
            style={{ borderRadius: 10, border: `1px solid ${C.steel}`, padding: '10px 14px', fontFamily: 'inherit', fontSize: 16, background: '#FFFFFF', resize: 'none' }} />
        </div>

        <div style={{ padding: '14px 16px 0' }}>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={pickPhoto} style={{ display: 'none' }}
            data-testid="problem-photo-input" aria-label={`Photo of the ${noun}`} />
          {photo ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img src={photo} alt="Goes with the report" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: `1px solid ${C.dim}` }} />
              <button type="button" onClick={() => setPhoto(null)}
                style={{ minHeight: 44, borderRadius: 10, border: `1px solid ${C.steel}`, background: '#FFFFFF', fontFamily: 'inherit', fontSize: 15, padding: '0 14px', cursor: 'pointer' }}>
                Remove the photo
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current && fileRef.current.click()}
              style={{ width: '100%', minHeight: 52, borderRadius: 12, border: '2px dashed #6B7280', background: '#FFFFFF', color: C.ink, fontFamily: 'inherit',
                fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>
              Add a photo of the {noun}
            </button>
          )}
        </div>

        <div style={{ padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor="pc-pin" style={{ fontSize: 15, fontWeight: 600 }}>Your PIN</label>
          <input id="pc-pin" type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)}
            style={{ ...input, letterSpacing: '0.3em' }} />
        </div>

        <div style={{ padding: '20px 16px 28px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {error && <div role="alert" style={{ color: C.red, fontWeight: 700, fontSize: 15 }}>{error}</div>}
          <button type="submit" disabled={busy}
            style={{ minHeight: 56, border: 0, borderRadius: 12, background: C.red, color: '#FFFFFF', fontFamily: 'inherit', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
            {busy ? 'Sending…' : 'Tell the office'}
          </button>
          <div style={{ fontSize: 13, color: C.sub, textAlign: 'center' }}>
            Posts to shop alerts with the photo and a link to this {noun}, and flags the {noun}{orderNumber ? ` on ${orderNumber}` : ''} until someone clears it.
          </div>
        </div>
      </form>
    </Overlay>
  );
}
